import { useState } from "react";
import { Icon } from "@/components/ui/icon";
import { ThemeToggle } from "@/components/ThemeToggle";

interface HeaderProps {
  profile?: {
    full_name: string;
    github_url?: string;
    linkedin_url?: string;
    instagram_url?: string;
    twitter_url?: string;
    dribbble_url?: string;
    behance_url?: string;
  } | null;
}

export const Header = ({ profile }: HeaderProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const initials = profile?.full_name
    ?.split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase() || "BL";

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border/80">
      <div className="max-w-6xl mx-auto container-padding">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="size-9 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold text-sm">
              {initials}
            </div>
            <span className="font-semibold text-base text-foreground">
              {profile?.full_name || "Resume"}
            </span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1 text-sm">
            {["hero", "skills", "projects", "experience", "education", "contact"].map((section) => (
              <button
                key={section}
                onClick={() => scrollToSection(section)}
                className="px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors capitalize"
              >
                {section === "hero" ? "Home" : section}
              </button>
            ))}

            {/* Social Links */}
            <div className="flex items-center gap-1 ml-2 pl-3 border-l border-border">
              {profile?.github_url && (
                <a
                  href={profile.github_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors"
                  aria-label="GitHub"
                >
                  <Icon name="github" size={18} />
                </a>
              )}
              {profile?.linkedin_url && (
                <a
                  href={profile.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors"
                  aria-label="LinkedIn"
                >
                  <Icon name="linkedin" size={18} />
                </a>
              )}
              {profile?.instagram_url && (
                <a
                  href={profile.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors"
                  aria-label="Instagram"
                >
                  <Icon name="instagram" size={18} />
                </a>
              )}
              {profile?.twitter_url && (
                <a
                  href={profile.twitter_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors"
                  aria-label="X (Twitter)"
                >
                  <Icon name="twitter" size={18} />
                </a>
              )}
              {profile?.dribbble_url && (
                <a
                  href={profile.dribbble_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors"
                  aria-label="Dribbble"
                >
                  <Icon name="dribbble" size={18} />
                </a>
              )}
              {profile?.behance_url && (
                <a
                  href={profile.behance_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors"
                  aria-label="Behance"
                >
                  <Icon name="behance" size={18} />
                </a>
              )}
              
              {/* Theme Toggle */}
              <ThemeToggle />
            </div>
          </nav>

          {/* Mobile Menu Button and Theme Toggle */}
          <div className="md:hidden flex items-center gap-1">
            <ThemeToggle />
            <button
              className="p-2 hover:bg-muted/50 rounded-lg transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <Icon name={mobileMenuOpen ? "close" : "menu"} size={22} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-border flex flex-col gap-1 animate-fade-in">
            {["hero", "skills", "projects", "experience", "education", "contact"].map((section) => (
              <button
                key={section}
                onClick={() => scrollToSection(section)}
                className="text-left py-2.5 px-3 text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-lg transition-colors capitalize"
              >
                {section === "hero" ? "Home" : section}
              </button>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};
